// "Save version" for the browser room.
//
// The room being saved has to REACH the server first, and which room that is
// depends on the mode:
//
//   CONNECTED — the jam is the room. Its state is already server-side, so a
//   save publishes that jam and nothing else. It used to create a SECOND jam
//   from the base seed and publish that instead, which threw away everything
//   the session had done.
//
//   LOCAL — the room is in the browser and has never been anywhere. A fresh
//   jam is created and the room is UPLOADED into it (`roomUpload`), which
//   works because a jam's Durable Object only hydrates from its base seed on
//   the first /state or /ws: intents posted before either find it empty and
//   become its content. It used to skip that step entirely, so publish read a
//   room freshly hydrated from the base seed and every version saved from the
//   browser was a byte-for-byte copy of its parent — while the dialog said
//   "Saved as v2" and the editor cleared its unsaved-changes flag, so the work
//   could then be navigated away from without even a warning.
//
// A local room's recorded takes have never left the tab either
// (hooks/useTakeStore.ts). Once the fresh jam exists they are uploaded into it
// and the room goes up naming their pool keys instead (lib/recording.ts
// `rekeyAudio`) — before the room, because a clip is created with its key.
//
// The save also brings a MASTER. A jam commit writes no audio of its own, so
// a version published without one is correct and unplayable — 0:00, a flat
// waveform, a dead play button. The browser renders the mixdown it is already
// holding and puts it in the pool (lib/publish-master.ts) before committing.
//
// Nothing here reports success it has not verified: the upload's rejections
// are checked, the room is counted, and the master is content-checked by the
// worker before the seed references it.

import { hashOfPoolKey } from "@shared/blobs.js";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Save } from "lucide-react";
import type { JamSummary } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Input } from "@web-shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@web-shared/components/ui/dialog";
import type { CanonicalRoom } from "@shared/sync/state.js";
import type { Intent, IntentEnvelope, IntentResponse } from "@shared/sync/intents.js";
import { createdLanesOf, roomUpload } from "@web-shared/lib/edits";
import { putPoolAudio } from "@/lib/pool-audio";
import { uploadMaster, type MasterProgress, type MasterUpload } from "@/lib/publish-master";
import { webApi, json } from "@web-shared/lib/web-api";
import { track as trackAnalytics } from "@web-shared/lib/analytics";
import { seedPath } from "@/lib/ctas";
import { localTakeKeysOf, rekeyAudio } from "@/lib/recording";
import { useAuthGate } from "../auth";

interface PublishResponse {
  seedId: string;
  version: number;
  tipAdvanced: boolean;
}

export function PublishRoom({
  seedId,
  seedTitle,
  dirty,
  room,
  jamId,
  localTakes,
  renderMaster,
  onPublished,
}: {
  seedId: string;
  seedTitle: string;
  dirty: boolean;
  /** Local mode: the room to upload. Ignored when `jamId` is given — a live
   * jam's room is already the server's. */
  room?: CanonicalRoom | null;
  /** Connected mode: the jam to publish. Absent ⇒ local mode. */
  jamId?: string;
  /** Local mode: takes recorded in this tab, by the `local/…` key clips name. */
  localTakes?: ReadonlyMap<string, Blob>;
  /** Render the room's mixdown — what the saved version plays. Absent ⇒ the
   * version is saved without one, as it always was. */
  renderMaster?: () => Promise<AudioBuffer>;
  onPublished?: () => void;
}) {
  const gate = useAuthGate();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [stage, setStage] = useState<MasterProgress | "saving" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PublishResponse | null>(null);

  const handleSave = () => gate.requireSignIn("save_version", "Sign in to save this version", () => setOpen(true));

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      // Connected: publish the room you are actually in. Local: make one and
      // put this room in it.
      setStage("saving");
      const targetJam = jamId ?? (await uploadRoom(seedId, room ?? null, localTakes));

      // The sound of it. A failed render must not lose the arrangement, so it
      // is a warning on the console and a version without a master — exactly
      // what saving did before — rather than a failed save.
      let master: MasterUpload | null = null;
      if (renderMaster) {
        try {
          master = await uploadMaster(targetJam, renderMaster, setStage);
        } catch (err) {
          console.warn("[Seedbed] the mixdown did not make it; saving the arrangement anyway:", err);
        }
      }

      // Uses /api/jam/ (singular, the DO route) not /api/jams/ (the list).
      setStage("saving");
      const pub = await webApi<PublishResponse>(
        `/jam/${encodeURIComponent(targetJam)}/publish`,
        json("POST", { note: note.trim() || undefined, ...(master ? { master } : {}) }),
      );

      setResult(pub);
      onPublished?.();
      trackAnalytics("remix_completed", { seedId, detail: "browser" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setPublishing(false);
      setStage(null);
    }
  };

  const busyLabel =
    stage === "rendering"
      ? "Rendering…"
      : stage === "uploading"
        ? "Uploading…"
        : stage === "verifying"
          ? "Checking…"
          : "Saving…";

  return (
    <>
      <Button
        size="sm"
        variant={dirty ? "default" : "outline"}
        onClick={handleSave}
        className="gap-1.5"
        title="Save version"
      >
        <Save className="size-3.5" />
        <span className="hidden sm:inline">Save version</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          {result ? (
            <div className="space-y-3">
              <DialogHeader>
                <DialogTitle>Saved as v{result.version}</DialogTitle>
                <DialogDescription>
                  Your remix of <strong>{seedTitle}</strong> is live.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => navigate(seedPath(result.seedId))}>
                  Open seed
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
                  Keep editing
                </Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Save as a new version</DialogTitle>
                <DialogDescription>
                  Creates a new version of <strong>{seedTitle}</strong>, credited to you.
                </DialogDescription>
              </DialogHeader>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional) — what changed?"
                autoFocus
                disabled={publishing}
              />
              {error && (
                <p className="text-xs text-removed">{error}</p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={publishing}>
                  Cancel
                </Button>
                <Button size="sm" onClick={publish} disabled={publishing}>
                  {publishing ? busyLabel : "Save"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A local room into a jam of its own, ready to publish. Returns the jam id.
 *
 * Throws rather than half-saving. A rejected intent here is not a warning —
 * it means the version about to be written is not the room the producer is
 * looking at, and the honest thing is to say so and write nothing.
 */
async function uploadRoom(
  seedId: string,
  local: CanonicalRoom | null,
  takes: ReadonlyMap<string, Blob> = new Map(),
): Promise<string> {
  if (!local) throw new Error("There is no room to save yet.");
  const jam = await webApi<JamSummary>(`/seeds/${encodeURIComponent(seedId)}/jams`, json("POST", {}));

  const uploaded = new Map<string, string>();
  for (const key of localTakeKeysOf(local)) {
    const blob = takes.get(key);
    if (!blob) continue; // rekeyAudio refuses the room below
    const up = await putPoolAudio(jam.id, new Uint8Array(await blob.arrayBuffer()));
    uploaded.set(key, up.key);
  }
  const room = rekeyAudio(local, uploaded);

  // Clips placed from the producer's own library are already stored, but no
  // jam holds them yet: take a reference for this one, so everyone who opens
  // it can hear them (the library is private; a placed clip is not).
  const libraryKeys = new Set(room.clips.filter((c) => c.libraryClip && c.audioKey).map((c) => c.audioKey!));
  for (const key of libraryKeys) {
    const at = key.indexOf("blobs/");
    const hash = at < 0 ? null : hashOfPoolKey(key, key.slice(0, at));
    if (hash) await webApi(`/jam/${encodeURIComponent(jam.id)}/files`, json("POST", { hash }));
  }

  const plan = roomUpload(room);
  const clientId = `web-publish-${Math.random().toString(36).slice(2, 10)}`;
  const send = async (intents: Intent[], baseRoomRev: number): Promise<IntentResponse> => {
    const envelope: IntentEnvelope = { v: 3, clientId, baseRoomRev, intents };
    return await webApi<IntentResponse>(`/jam/${encodeURIComponent(jam.id)}/intents`, json("POST", envelope));
  };

  // The room's shape first — an empty room adopts it — then the lane fields,
  // which need ids only the server can give (`trackCreated.tempId`).
  const created = await send(plan.create, 0);
  refuseRejections(created);
  const lanes = createdLanesOf(created.accepted);
  if (lanes.size !== plan.expect.tracks) {
    throw new Error(`Only ${lanes.size} of ${plan.expect.tracks} tracks reached the server — nothing was saved.`);
  }
  const fields = plan.fields(lanes);
  if (fields.length > 0) refuseRejections(await send(fields, created.roomRev));

  return jam.id;
}

function refuseRejections(res: IntentResponse): void {
  if (res.rejected.length === 0) return;
  const first = res.rejected[0];
  throw new Error(
    `The server refused ${res.rejected.length} change${res.rejected.length === 1 ? "" : "s"}` +
      `${first ? ` (${first.reason})` : ""} — nothing was saved.`,
  );
}
